import { useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash, Plus, ArrowRight } from "@phosphor-icons/react";

const MAX_PER_SLOT = 5;

function normalizePhotos(task, slot) {
  const arr = (task[`photos_${slot}`] || []).filter((p) => p && p.id && p.path);
  const legacy = task[`photo_${slot}_path`];
  if (arr.length === 0 && legacy) {
    return [{ id: `legacy-${slot}`, path: legacy }];
  }
  return arr;
}

function PhotoTile({ url, onDelete, label, index }) {
  return (
    <div className="relative aspect-square rounded-md overflow-hidden group border border-white/10" data-testid={`photo-tile-${label}-${index}`}>
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={`${label} ${index + 1}`} loading="lazy" className="w-full h-full object-cover" />
      </a>
      <button
        onClick={onDelete}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 focus:opacity-100 transition flex items-center justify-center text-red-300 hover:bg-red-500/80 hover:text-white"
        data-testid={`photo-delete-${label}-${index}`}
      >
        <Trash size={11} />
      </button>
    </div>
  );
}

function Slot({ slot, label, tone, task, uploading, onUpload, onDelete }) {
  const ref = useRef(null);
  const photos = normalizePhotos(task, slot);
  const token = ""; // httpOnly cookie auth
  const canAdd = photos.length < MAX_PER_SLOT;
  const empty = photos.length === 0;

  const isUploadingHere = uploading === slot;
  const tint = tone === "amber"
    ? "border-amber-500/40 bg-amber-500/5 hover:border-amber-500"
    : "border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-500";

  return (
    <div className="flex-1 min-w-0" data-testid={`task-slot-${slot}-${task.id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
        {!empty && <div className="text-[9px] text-white/30 font-mono">{photos.length}/{MAX_PER_SLOT}</div>}
      </div>
      {empty ? (
        <button
          onClick={() => ref.current?.click()}
          disabled={isUploadingHere}
          className={`aspect-square w-full rounded-lg border-2 border-dashed ${tint} flex flex-col items-center justify-center text-[10px] text-white/60 active:scale-95 disabled:opacity-40`}
          data-testid={`task-photo-upload-${slot}-${task.id}`}
        >
          {isUploadingHere ? <div className="w-4 h-4 border border-white/40 border-t-white rounded-full animate-spin" /> : <Camera size={20} weight="duotone" />}
          <span className="mt-1">{isUploadingHere ? "…" : "Ajouter"}</span>
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-1">
          {photos.map((p, i) => {
            const url = `${API}/tasks/${task.id}/photo/${slot}/${p.id}?auth=${encodeURIComponent(token)}`;
            return (
              <PhotoTile
                key={p.id}
                url={url}
                index={i}
                label={slot}
                onDelete={() => onDelete(slot, p.id)}
              />
            );
          })}
          {canAdd && (
            <button
              onClick={() => ref.current?.click()}
              disabled={isUploadingHere}
              className={`aspect-square rounded-md border-2 border-dashed ${tint} flex items-center justify-center text-white/50 active:scale-95 disabled:opacity-40`}
              data-testid={`task-photo-add-${slot}-${task.id}`}
              title="Ajouter une photo"
            >
              {isUploadingHere ? <div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin" /> : <Plus size={16} weight="bold" />}
            </button>
          )}
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => { onUpload(slot, Array.from(e.target.files || [])); e.target.value = ""; }}
      />
    </div>
  );
}

export default function TaskBeforeAfter({ task, onUpdate }) {
  const [uploading, setUploading] = useState(null);

  const upload = async (slot, files) => {
    if (!files || files.length === 0) return;
    const existing = normalizePhotos(task, slot).length;
    const room = MAX_PER_SLOT - existing;
    if (room <= 0) { toast.error(`Max ${MAX_PER_SLOT} photos ${slot === "before" ? "avant" : "après"}`); return; }
    const chosen = files.slice(0, room);
    setUploading(slot);
    try {
      for (const file of chosen) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/tasks/${task.id}/photo/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const overflow = files.length - chosen.length;
      toast.success(`${chosen.length} photo${chosen.length > 1 ? "s" : ""} ${slot === "before" ? "avant" : "après"} enregistrée${chosen.length > 1 ? "s" : ""}` + (overflow > 0 ? ` · ${overflow} ignorée${overflow > 1 ? "s" : ""} (max ${MAX_PER_SLOT})` : ""));
      onUpdate?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(null); }
  };

  const del = async (slot, photoId) => {
    if (!window.confirm(`Supprimer cette photo ${slot === "before" ? "avant" : "après"} ?`)) return;
    try {
      if (photoId.startsWith("legacy-")) {
        // Legacy single-slot endpoint (no photo id in URL)
        await api.delete(`/tasks/${task.id}/photo/${slot}`);
      } else {
        await api.delete(`/tasks/${task.id}/photo/${slot}/${photoId}`);
      }
      onUpdate?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mt-2 flex items-start gap-2">
      <Slot slot="before" label="Avant" tone="amber" task={task} uploading={uploading} onUpload={upload} onDelete={del} />
      <ArrowRight size={14} className="text-white/30 shrink-0 mt-8" />
      <Slot slot="after" label="Après" tone="emerald" task={task} uploading={uploading} onUpload={upload} onDelete={del} />
    </div>
  );
}
